/**
 * MEDIA SERVER CONFIGURATION
 * 
 * To enable/disable media server:
 * 1. Set useMediaServer to true/false (line ~95)
 * 2. Update MEDIA_SERVER_CONFIG in src/hooks/useMediaServerRecording.js
 * 
 * When enabled: Video streams to media server → S3 (processed on server)
 * When disabled: Recording is not available
 * 
 * MICROPHONE AUTO-CLOSE (BACKEND VAD)
 * 
 * The microphone automatically closes when the backend detects speech has ended.
 * Flow:
 * 1. User speaks → Frontend streams audio to backend
 * 2. Backend processes via Google Speech-to-Text (with built-in VAD)
 * 3. When Google STT detects speech end → Backend sends 'final_consolidated_transcript'
 * 4. Frontend receives message → Automatically closes microphone
 * 
 * Local VAD is DISABLED (enableAutoClose: false) - Backend controls mic closure.
 * This provides more accurate speech detection using Google's VAD instead of local browser detection.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { API } from "aws-amplify";
import awsExports from "../aws-exports";
import SineWaves from "sine-waves";
import {
  listCandidateProfiles,
  listInterviewSlots,
  getInterviewSlot,
  getCompanyJobDescription,
  listSpotJDS,
} from "../graphql/queries";
// Note: createInterviewEvaluation import removed as it's handled by utility functions
import {
  storeInterviewDataLocally,
  attemptAWSStorage,
} from "../utils/evaluationStorage";
// Note: TOKEN_STATUS import removed as it's not used in this refactored version

// Custom hooks
import { useWebSocket, MESSAGE_TYPES } from "../hooks/useWebSocket";
import { useEnhancedAudioRecording, AUDIO_MODES } from "../hooks/useEnhancedAudioRecording";
import { useInterviewState } from "../hooks/useInterviewState";
import { useMediaServerRecording } from "../hooks/useMediaServerRecording";

// Utilities
import { handleWebSocketMessage } from "../utils/messageHandlers";
import { 
  playAudioFromBase64, 
  stopAudio, 
  formatTime, 
  getOptimalAudioConstraints 
} from "../utils/audioUtils";
import { createAudioMessage, getOptimalChirpAudioConstraints } from "../utils/audioMessaging";
import { 
  isScreeningRound, 
  extractResumeDataForScreening, 
  markScreeningCompleted,
  fetchCandidateProfileForScreening 
} from "../utils/screeningUtils";

// UI Components and Icons
import { BsCameraVideo, BsCameraVideoOff } from "react-icons/bs";
import { BiMicrophone, BiMicrophoneOff } from "react-icons/bi";
import { BsClock } from "react-icons/bs";
import { MdCallEnd } from "react-icons/md";
import { FaVideo, FaMicrophone } from "react-icons/fa";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { FaRegCheckCircle } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Assets
import logo from "../assets/logogetscreened.svg";
import "./Interview.css";

function Interview() {
  // ============ DATA STATE ============
  const [candidateData, setCandidateData] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [interviewSlotData, setInterviewSlotData] = useState(null);
  const [candidateEmail, setCandidateEmail] = useState(null);
  const [currentInterviewSlotId, setCurrentInterviewSlotId] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [dataLoadError, setDataLoadError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isValidating, setIsValidating] = useState(true);
  const [interviewType, setInterviewType] = useState('jd_specific'); // 'screening' or 'jd_specific'

  // ============ UI STATE ============
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [showRules, setShowRules] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [loaderText, setLoaderText] = useState("Processing...");
  const [captionTone, setCaptionTone] = useState("light");

  // ============ CONVERSATION STATE ============
  const [messages, setMessages] = useState([]);
  const [conversationTranscript, setConversationTranscript] = useState([]);
  const [botSpeechCaption, setBotSpeechCaption] = useState("");
  const [streamingTranscript, setStreamingTranscript] = useState("");
  const [liveCaption, setLiveCaption] = useState("");

  // ============ TYPING ANIMATION STATE ============
  const [typingText, setTypingText] = useState("");
  const [fullTypingText, setFullTypingText] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  // ============ AUDIO STATE ============
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isAITalking, setIsAITalking] = useState(false);
  const [currentAudioElement, setCurrentAudioElement] = useState(null);
  const [backendSTTEnabled] = useState(true); // Re-enabled for enhanced backend STT with raw audio streaming

  // ============ VIDEO RECORDING STATE ============
  const [screenRecordingUrl, setScreenRecordingUrl] = useState(null);
  const [screenRecordingInitiated, setScreenRecordingInitiated] = useState(false);
  const [pendingVideoUrl, setPendingVideoUrl] = useState(null);
  const [videoUrlSent, setVideoUrlSent] = useState(false);
  const [videoUrlRetryCount, setVideoUrlRetryCount] = useState(0);
  const [isAwaitingVideoUpload, setIsAwaitingVideoUpload] = useState(false);
  const [isConclusionAudioPlaying, setIsConclusionAudioPlaying] = useState(false);
  
  // Media Server states
  const [mediaServerRecordingUrl, setMediaServerRecordingUrl] = useState(null);
  const [useMediaServer, setUseMediaServer] = useState(true); // Direct config - set to false to disable
  const [mediaServerUploadProgress, setMediaServerUploadProgress] = useState(0);
  const [isMediaServerUploadComplete, setIsMediaServerUploadComplete] = useState(false);
  const [isDemoInterview, setIsDemoInterview] = useState(false);

  // ============ SECURITY STATE ============
  const [securityViolationDetected, setSecurityViolationDetected] = useState(false);
  const [isSecurityActive, setIsSecurityActive] = useState(false);

  // ============ SESSION STATE ============
  const [sessionId] = useState(() => Date.now() + '-' + Math.random().toString(36).substr(2, 9));
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [lastConnectionTime, setLastConnectionTime] = useState(null);
  const [hasReceivedInitialResponse, setHasReceivedInitialResponse] = useState(false);
  const [startInterviewSent, setStartInterviewSent] = useState(false);

  // ============ WEBHOOK STATE ============
  // Webhook functionality removed

  // ============ WEBHOOK DEBUG HELPERS ============
  // ============ REFS ============
  const videoRef = useRef(null);
  const screenRecorderRef = useRef(null);
  const backendSTTFallbackTimerRef = useRef(null);
  const captionSamplingCanvasRef = useRef(null);
  const captionSamplingFailedRef = useRef(false);
  const aiWaveCanvasRef = useRef(null);
  const sineWavesInstanceRef = useRef(null);

  // ============ CUSTOM HOOKS ============
  
  // Calculate dynamic timer based on interviewType
  // screening = 20 minutes, jd_specific = 30 minutes
  const calculateInterviewDuration = useCallback(() => {
    // Priority order: 1) explicit interviewType from localStorage, 2) state, 3) isScreeningRound flag
    const storedType = localStorage.getItem('interviewType');
    const isScreeningRound = localStorage.getItem('isScreeningRound') === 'true';
    
    // Determine the interview type with proper priority
    let type;
    
    if (storedType) {
      // Highest priority: explicitly stored interview type
      type = storedType;
    } else if (interviewType && interviewType !== 'jd_specific') {
      // Second priority: state if it's been explicitly set (not default)
      type = interviewType;
    } else if (isScreeningRound) {
      // Third priority: screening flag (legacy support)
      type = 'screening';
    } else {
      // Default: use current state or fall back to jd_specific
      type = interviewType || 'jd_specific';
    }
    
    // Set duration based on interview type
    const durationInSeconds = type === 'screening' ? (20 * 60) : (30 * 60);
    
    return durationInSeconds;
  }, [interviewType]);
  
  // Memoize initial interview duration to prevent recalculation on every render
  const initialInterviewDuration = useMemo(() => {
    const storedType = localStorage.getItem('interviewType');
    const isScreeningRound = localStorage.getItem('isScreeningRound') === 'true';
    const type = storedType || (isScreeningRound ? 'screening' : 'jd_specific');
    const duration = type === 'screening' ? (20 * 60) : (30 * 60);
    console.log(`⏱️ Initial interview timer set: ${type} = ${duration / 60} minutes`);
    return duration;
  }, []); // Only calculate once on mount
  
  // Interview state management
  const interviewState = useInterviewState({
    onStateChange: (state) => {
      // State change logging removed to reduce console spam
    },
    initialTimeRemaining: initialInterviewDuration
  });
  
  // Audio constraints optimized for Google Chirp model
  const audioConstraints = useMemo(() => getOptimalChirpAudioConstraints(), []);

  // Typing animation effect for AI responses
  useEffect(() => {
    if (!fullTypingText) {
      setTypingText("");
      setIsTyping(false);
      return;
    }

    setIsTyping(true);
    setTypingText("");
    let currentIndex = 0;

    const typingInterval = setInterval(() => {
      if (currentIndex <= fullTypingText.length) {
        setTypingText(fullTypingText.slice(0, currentIndex));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 50); // Adjust speed by changing this value (50ms per character)

    return () => clearInterval(typingInterval);
  }, [fullTypingText]);

  // Function to start typing animation
  const startTypingAnimation = useCallback((text) => {
    if (text) {
      setFullTypingText(text);
    } else {
      setFullTypingText("");
      setTypingText("");
      setIsTyping(false);
    }
  }, []);

  // WebSocket message handler function (declared before websocket hook)
  const websocketMessageHandler = useCallback((data, event) => {
    // This will be safe since by the time messages arrive, the context will be created
    try {
      // Find the current messageHandlerContext - we'll update this via useEffect
      if (window.currentMessageHandlerContext) {
        handleWebSocketMessage(data, window.currentMessageHandlerContext);
      } else {
        // Queue the message for later processing
        if (!window.pendingWebSocketMessages) window.pendingWebSocketMessages = [];
        window.pendingWebSocketMessages.push({ data, event });
      }
    } catch (error) {
      console.error('❌ Error handling WebSocket message:', error);
    }
  }, []);

  // WebSocket connection (declare this FIRST before audio recording)
  const websocket = useWebSocket({
    url: 'wss://phone.getscreened.in/getscreenedbot/ws',
    onMessage: websocketMessageHandler,
    autoConnect: false, // We'll connect manually when ready
    reconnectAttempts: 3,
    reconnectDelay: 1000
  });

  // Media server recording hook
  const mediaServerRecording = useMediaServerRecording({
    interviewId: currentInterviewSlotId,
    candidateId: candidateData?.id,
    userId: candidateData?.user_id,
    sessionId,
    isAITalking, // Pass AI speaking state to control microphone audio
    audioConstraints, // Pass audio constraints for microphone
    onRecordingComplete: (recordingUrl, statistics) => {
      console.log('✅ Media server recording completed:', recordingUrl);
      setMediaServerRecordingUrl(recordingUrl);
      setIsMediaServerUploadComplete(true);
      setMediaServerUploadProgress(100);
    },
    onProgress: (progress) => {
      console.log(`📤 Media server upload progress: ${progress}%`);
      setMediaServerUploadProgress(progress);
    },
    onError: (error) => {
      console.error('❌ Media server recording error:', error);
    }
  });

  // ============ AUDIO CHUNK HANDLING FOR BACKEND STT ============

  // Rate limiting state for audio chunks
  const lastChunkSentRef = useRef(0);
  const chunksInLastSecond = useRef(0);
  const chunkTimeWindow = useRef(Date.now());

  // DISABLED: Function to send audio chunks to backend for real-time STT
  // We now use Web Speech API for client-side transcription instead
  /*
  const sendLiveAudioChunk = useCallback(async (audioData, metadata = {}) => {
    if (!websocket?.isConnected || !backendSTTEnabled) {
      console.warn('⚠️ Cannot send audio chunk - WebSocket not connected or STT disabled');
      return false;
    }

    // Rate limiting: max 5 chunks per second
    const now = Date.now();
    if (now - chunkTimeWindow.current > 1000) {
      // Reset counter every second
      chunkTimeWindow.current = now;
      chunksInLastSecond.current = 0;
    }

    if (chunksInLastSecond.current >= 5) {
      console.warn('⚠️ Rate limit: Too many audio chunks per second, dropping chunk');
      return false;
    }

    // Minimum interval between chunks: 200ms
    if (now - lastChunkSentRef.current < 200) {
      console.warn('⚠️ Rate limit: Too frequent chunks, dropping chunk');
      return false;
    }

    try {
      let base64Audio;
      
      // Handle different audio data types
      if (audioData instanceof Blob) {
        const arrayBuffer = await audioData.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        // Process in chunks to avoid stack overflow
        let binaryString = '';
        const chunkSize = 8192;
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.subarray(i, i + chunkSize);
          binaryString += String.fromCharCode.apply(null, chunk);
        }
        base64Audio = btoa(binaryString);
      } else if (audioData instanceof ArrayBuffer) {
        const uint8Array = new Uint8Array(audioData);
        // Process in chunks to avoid stack overflow
        let binaryString = '';
        const chunkSize = 8192;
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.subarray(i, i + chunkSize);
          binaryString += String.fromCharCode.apply(null, chunk);
        }
        base64Audio = btoa(binaryString);
      } else if (audioData instanceof Int16Array || audioData instanceof Uint8Array) {
        const uint8Array = new Uint8Array(audioData.buffer);
        // Process in chunks to avoid stack overflow
        let binaryString = '';
        const chunkSize = 8192;
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.subarray(i, i + chunkSize);
          binaryString += String.fromCharCode.apply(null, chunk);
        }
        base64Audio = btoa(binaryString);
      } else {
        console.error('❌ Unsupported audio data type:', typeof audioData);
        return false;
      }

      const {
        isInterim = true,
        sequenceNumber = Date.now(),
        timestamp = Date.now(),
        hasSpeech = false,
        bufferSize = 0,
        isFinal = false,
        duration = 0,
        manualStop = false,
        emptyFinalChunk = false
      } = metadata;

      // Skip tiny chunks unless they're final or manual stops
      if (bufferSize < 1024 && !isFinal && !manualStop) {
        console.warn('⚠️ Skipping tiny audio chunk:', bufferSize);
        return false;
      }

      const message = {
        type: 'audio_chunk_live',
        interview_id: currentInterviewSlotId,
        session_id: sessionId,
        audio: base64Audio,
        sequence: sequenceNumber,
        is_interim: isInterim,
        timestamp: timestamp,
        has_speech: hasSpeech,
        buffer_size: bufferSize,
        duration: duration,
        is_final: isFinal,
        format: "wav",
        sample_rate: 16000,
        channels: 1,
        bit_depth: 16,
        manual_stop: manualStop,
        empty_final_chunk: emptyFinalChunk
      };

      websocket.sendMessage(message);
      
      // Update rate limiting counters
      lastChunkSentRef.current = now;
      chunksInLastSecond.current++;
      
      console.log(`🎵 Audio chunk sent (seq: ${sequenceNumber}, interim: ${isInterim}, speech: ${hasSpeech}, size: ${bufferSize}, duration: ${duration}ms, avgLevel: ${metadata.averageLevel?.toFixed(4) || 'N/A'}, silenceTime: ${metadata.timeSinceLastSpeech || 0}ms${manualStop ? ', MANUAL_STOP' : ''}${emptyFinalChunk ? ', EMPTY_FINAL' : ''})`);
      return true;

    } catch (error) {
      console.error('❌ Failed to send audio chunk:', error);
      return false;
    }
  }, [websocket, backendSTTEnabled, currentInterviewSlotId, sessionId]);
  */

  // Function to send transcripts directly to backend (replaces audio chunks)
  const sendTranscriptToBackend = useCallback((transcript) => {
    if (!websocket?.isConnected) {
      console.warn('⚠️ Cannot send transcript - WebSocket not connected');
      return false;
    }

    try {
      const message = {
        type: 'user_transcript',
        interview_id: currentInterviewSlotId,
        session_id: sessionId,
        transcript: transcript.trim(),
        timestamp: Date.now(),
        source: 'web_speech_api'
      };

      websocket.sendMessage(message);
      console.log('📝 Transcript sent to backend:', transcript);
      return true;

    } catch (error) {
      console.error('❌ Failed to send transcript:', error);
      return false;
    }
  }, [websocket, currentInterviewSlotId, sessionId]);

  // Enhanced audio recording with Google STT backend service only
  const audioRecording = useEnhancedAudioRecording({
    mode: AUDIO_MODES.BACKEND_STT, // Use only Google STT service via backend
    audioConstraints,
    vadConfig: {
      silenceThreshold: 4.0,      // Not used - backend handles VAD via Google STT
      maxSpeakingTime: 90.0,      // Not used - backend handles VAD via Google STT
      warningDelay: 1.5,          // Not used - backend handles VAD via Google STT
      enableAutoClose: false      // ❌ DISABLED - Backend VAD controls mic closure via final_consolidated_transcript
    },
    onVADEvent: (vadEvent) => {
      // VAD events disabled - backend controls mic closure via final_consolidated_transcript
      // Keeping handler for compatibility with useEnhancedAudioRecording hook
    },
    onTranscript: (transcript) => {
      // Reduce logging - only log if transcript is substantial
      if (transcript.length > 10) {
        console.log('📝 Final transcript from Google STT Backend:', transcript.substring(0, 50) + (transcript.length > 50 ? '...' : ''));
      }
      
      // Add to messages and conversation transcript
      setMessages(prev => [...prev, { type: "user", text: transcript, source: "google_stt" }]);
      setConversationTranscript(prev => [
        ...prev,
        {
          type: "user",
          text: transcript,
          timestamp: new Date().toLocaleTimeString(),
          source: "google_stt"
        }
      ]);
      
      // Note: Transcript will be sent via live audio streaming automatically
      // Reduce logging for successful transcript processing
    },
    onLiveCaption: (caption) => {
      // This won't be used in backend STT mode, but keeping for compatibility
      // Removed logging to reduce spam
    },
    onStreamingTranscript: (transcript) => {
      // Removed logging to reduce spam - streaming transcript is shown in UI
      // Streaming transcript is now shown in user speaking indicator
    },
    onAudioChunk: (chunkData) => {
      // Send enhanced audio chunks to backend for real-time STT
      if (websocket.isConnected && candidateEmail && currentInterviewSlotId && backendSTTEnabled) {
        try {
          const audioMessage = createAudioMessage({
            base64Audio: chunkData.audio,
            sequence: chunkData.sequence,
            chunkInfo: chunkData.chunk_info,
            sessionInfo: {
              interview_id: currentInterviewSlotId,
              candidate_id: candidateEmail,
              session_id: sessionId
            },
            format: chunkData.format,
            useEnhancedFormat: true
          });

          console.log(`� Sending enhanced audio chunk ${chunkData.sequence} (${chunkData.chunk_info.duration_ms}ms, speech: ${chunkData.chunk_info.has_speech})`);
          websocket.sendMessage(audioMessage);
        } catch (error) {
          console.error('❌ Failed to send audio chunk:', error);
        }
      } else {
        console.debug('🔇 Audio chunk not sent - conditions not met:', {
          wsConnected: websocket.isConnected,
          hasEmail: !!candidateEmail,
          hasSlotId: !!currentInterviewSlotId,
          backendSTTEnabled
        });
      }
    },
    onError: (error) => {
      console.error('❌ Enhanced audio recording error:', error);
      toast.error('Audio recording error. Please check your microphone.');
    },
    backendConfig: {
      chunkDurationMs: 2000, // 2 second chunks for optimal Chirp performance
      enableSpeechDetection: true,
      qualityMetrics: true
    }
  });

  // Clear typing animation when microphone opens
  useEffect(() => {
    if (audioRecording?.isRecording) {
      setFullTypingText("");
      setTypingText("");
      setIsTyping(false);
    }
  }, [audioRecording?.isRecording]);

  // ============ CRITICAL FUNCTIONS FROM ORIGINAL INTERVIEW.JS ============

  // ============ INTERVIEW COMPLETION WEBHOOK ============

  // Webhook functionality removed per security requirements

  /**
   * Webhook beacon removed per security requirements
   */

  /**
   * Webhook recovery removed per security requirements
   */

  // ============ CRITICAL FUNCTIONS FROM ORIGINAL INTERVIEW.JS ============

  // Video URL management with retry logic (critical for interview completion)
  const sendVideoUrlToBackend = useCallback(async (videoUrl, interviewId, maxRetries = 3) => {
    // Check if already sent
    if (videoUrlSent) {
      console.log('🎥 Video URL already sent successfully, skipping');
      return true;
    }

    // Check if interview is completed or terminated
    if (interviewState.isCompleted || interviewState.isTerminated) {
      console.log('🎥 Interview already completed/terminated, no need to send video URL');
      return false;
    }

    const messageToSend = {
      type: "screen_recording_complete",
      videoURL: videoUrl,
      interview_id: interviewId,
      timestamp: new Date().toISOString(),
    };

    // Try sending via WebSocket
    if (websocket?.isConnected) {
      try {
        websocket.sendMessage(messageToSend);
        console.log('✅ Successfully sent video URL to backend');
        setVideoUrlSent(true);
        setPendingVideoUrl(null);
        setVideoUrlRetryCount(0);
        
        // Clean up stored fallback data
        cleanupStoredVideoUrls(interviewId);
        return true;
      } catch (sendError) {
        console.error('❌ Failed to send video URL:', sendError);
      }
    }

    // Retry logic if not already at max retries
    if (videoUrlRetryCount < maxRetries && !interviewState.isCompleted && !interviewState.isTerminated) {
      setPendingVideoUrl(videoUrl);
      setVideoUrlRetryCount(prev => prev + 1);
      
      console.log(`🔄 Scheduling retry ${videoUrlRetryCount + 1}/${maxRetries} in 2 seconds...`);
      
      const timeoutId = setTimeout(() => {
        if (!videoUrlSent && !interviewState.isCompleted && !interviewState.isTerminated) {
          sendVideoUrlToBackend(videoUrl, interviewId, maxRetries);
        }
      }, 2000);
      
      // Store timeout for cleanup
      if (!window.videoUrlRetryTimeouts) window.videoUrlRetryTimeouts = [];
      window.videoUrlRetryTimeouts.push(timeoutId);
      
      return false;
    } else {
      // Max retries exceeded, store as fallback
      console.error('❌ Max retries exceeded, storing video URL in localStorage as fallback');
      try {
        const fallbackData = {
          videoURL: videoUrl,
          interview_id: interviewId,
          timestamp: new Date().toISOString(),
          failureReason: 'max_retries_exceeded'
        };
        localStorage.setItem(`failed_termination_${interviewId}`, JSON.stringify(fallbackData));
      } catch (error) {
        console.error('❌ Failed to store fallback video URL:', error);
      }
    }

    return false;
  }, [videoUrlSent, interviewState, websocket, videoUrlRetryCount, setVideoUrlSent, setPendingVideoUrl, setVideoUrlRetryCount]);

  // Enhanced cleanup function for stored video URLs
  const cleanupStoredVideoUrls = useCallback((interviewId) => {
    if (!interviewId) return;
    try {
      const keysToRemove = [
        `pending_video_url_${interviewId}`,
        `failed_termination_${interviewId}`,
        `video_url_acknowledged_${interviewId}`,
        `interview_completed_${interviewId}` // Also clean completion flag if needed
      ];
      keysToRemove.forEach((key) => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          console.log(`🧹 Cleaned up stored data: ${key}`);
        }
      });
    } catch (error) {
      console.error("❌ Error cleaning up stored video URLs:", error);
    }
  }, []);

  // Clear all retry timeouts and reset retry state
  const clearAllRetries = useCallback(() => {
    console.log('🧹 Clearing all video URL retries');
    
    // Clear all timeout IDs
    if (window.videoUrlRetryTimeouts) {
      window.videoUrlRetryTimeouts.forEach(clearTimeout);
      window.videoUrlRetryTimeouts = [];
    }
    
    // Clear conclusion timeout
    if (window.conclusionTimeoutId) {
      clearTimeout(window.conclusionTimeoutId);
      window.conclusionTimeoutId = null;
    }
    
    // Reset retry state
    setVideoUrlRetryCount(0);
    setPendingVideoUrl(null);
    
    // Set global flag to prevent new retries
    window.interviewCompletedNoMoreRetries = true;
  }, [setVideoUrlRetryCount, setPendingVideoUrl]);

  // Enhanced conclusion acknowledgment with video URL handling
  const createConclusionAcknowledgment = useCallback(() => {
    return async () => {
      console.log('🎵 Executing conclusion acknowledgment');
      
      // Prevent duplicate acknowledgments
      if (window.conclusionAcknowledgmentInProgress) {
        console.log('🎵 Conclusion acknowledgment already in progress');
        return;
      }
      window.conclusionAcknowledgmentInProgress = true;

      try {
        // Send video URL if available and not sent
        if (mediaServerRecordingUrl && !videoUrlSent) {
          console.log('🎥 Sending video URL with conclusion acknowledgment');
          await sendVideoUrlToBackend(mediaServerRecordingUrl, currentInterviewSlotId);
        }

        // Send conclusion acknowledgment message
        if (websocket?.isConnected) {
          const conclusionAck = {
            type: "conclusion_played",
            interview_id: currentInterviewSlotId,
            timestamp: new Date().toISOString(),
          };
          
          try {
            websocket.sendMessage(conclusionAck);
            console.log('✅ Sent conclusion acknowledgment');
          } catch (error) {
            console.error('❌ Failed to send conclusion acknowledgment:', error);
          }
        }

        // Clear all retries since interview is concluding
        clearAllRetries();

        // Mark interview as complete
        setTimeout(() => {
          interviewState.completeInterview();
          setIsConclusionAudioPlaying(false);
          
          // Mark screening as completed if this was a screening round
          const wasScreeningRound = localStorage.getItem('isScreeningRound') === 'true';
          if (wasScreeningRound) {
            const candidateEmail = localStorage.getItem('candidateEmail');
            if (candidateEmail) {
              markScreeningCompleted(candidateEmail);
              console.log('✅ Screening round marked as completed for:', candidateEmail);
            }
            // Clean up screening-specific localStorage items
            localStorage.removeItem('isScreeningRound');
            localStorage.removeItem('screeningSlotId');
          }
        }, 1000);

      } catch (error) {
        console.error('❌ Error in conclusion acknowledgment:', error);
      } finally {
        window.conclusionAcknowledgmentInProgress = false;
      }
    };
  }, [
    mediaServerRecordingUrl, 
    videoUrlSent, 
    sendVideoUrlToBackend, 
    currentInterviewSlotId, 
    websocket, 
    interviewState, 
    clearAllRetries,
    setIsConclusionAudioPlaying
  ]);

  // Recovery function for failed video URL sends (runs on WebSocket connect)
  const recoverFailedVideoUrlSends = useCallback(async () => {
    const interviewId = currentInterviewSlotId;
    if (!interviewId) return;

    try {
      // Check for stored failed termination data
      const failedData = localStorage.getItem(`failed_termination_${interviewId}`);
      if (failedData && !videoUrlSent) {
        const data = JSON.parse(failedData);
        console.log('🔄 Recovering failed video URL send:', data);
        await sendVideoUrlToBackend(data.videoURL, interviewId, 2);
      }
    } catch (error) {
      console.error('❌ Error recovering failed video URL sends:', error);
    }
  }, [currentInterviewSlotId, videoUrlSent, sendVideoUrlToBackend]);

  // ============ WEBSOCKET MESSAGE HANDLER CONTEXT ============

  // WebSocket message handler context (now websocket and audioRecording are available)
  const messageHandlerContext = useMemo(() => ({
    // State setters
    setMessages,
    setConversationTranscript,
    setShowLoader,
    setLoaderText,
    setBotSpeechCaption: startTypingAnimation, // Use typing animation
    setVideoUrlSent,
    setPendingVideoUrl,
    setVideoUrlRetryCount,
    setIsConclusionAudioPlaying,
    setIsAITalking,
    setStreamingTranscript,
    setLiveCaption,
    
    // State values
    backendSTTEnabled,
    isTerminationInProgress: interviewState.isTerminating,
    isInterviewTerminated: interviewState.isTerminated,
    isInterviewCompleted: interviewState.isCompleted,
    
    // Session management
    sessionId,
    hasReceivedInitialResponse,
    setHasReceivedInitialResponse,
    
    // Audio recording management
    audioRecording,
    
    // Enhanced playAudio function with proper microphone management
    playAudio: (base64Audio, format, text, callback) => {
      console.log('🎵 Playing audio:', { hasAudio: !!base64Audio, format, hasText: !!text });
      
      // Start typing animation with the text
      if (text) {
        startTypingAnimation(text);
      }
      
      const audio = playAudioFromBase64(base64Audio, format, text, callback, {
        setShowLoader,
        setIsSpeaking,
        setBotSpeechCaption: startTypingAnimation, // Use typing animation instead
        setConversationTranscript,
        onAudioStart: () => {
          setIsSpeaking(true);
        },
        onAudioEnd: () => {
          setIsSpeaking(false);
          startTypingAnimation(""); // Clear typing animation when audio ends
        }
      });
      setCurrentAudioElement(audio);
      return audio;
    },
    
    // WebSocket communication (will be updated via useEffect)
    sendMessage: (message) => {
      console.log('📤 Sending message via context:', message);
      // Will be updated after WebSocket is initialized
    },
    
    // Utility functions
    clearBackendSTTFallbackTimer: () => {
      if (backendSTTFallbackTimerRef.current) {
        clearTimeout(backendSTTFallbackTimerRef.current);
        backendSTTFallbackTimerRef.current = null;
      }
    },
    
    getInterviewSlotId: () => currentInterviewSlotId || 'unknown_slot',
    
    // Critical functions from original Interview.js
    cleanupStoredVideoUrls,
    sendVideoUrlToBackend,
    clearAllRetries,
    createConclusionAcknowledgment,
    recoverFailedVideoUrlSends: async () => {
      await recoverFailedVideoUrlSends();
    },
    
    // Recording control
    stopAllRecording: () => {
      console.log('🛑 Stopping all recording');
      // Direct access to audioRecording to avoid dependency loops
      if (audioRecording && typeof audioRecording.stopRecording === 'function') {
        audioRecording.stopRecording();
      }
    },
    
    stopScreenRecording: () => {
      // Stop media server recording if enabled
      if (useMediaServer && mediaServerRecording.isRecording) {
        mediaServerRecording.stopRecording();
        console.log('📺 Media server recording stopped');
        return;
      }
      
      // Fallback to original screen recording stop
      if (screenRecorderRef.current && screenRecorderRef.current.state !== "inactive") {
        try {
          screenRecorderRef.current.stop();
          console.log('🎥 Screen recording stopped');
        } catch (err) {
          console.error("🎥 Failed to stop screen recording:", err);
        }
      }
    },
    
    // Audio control
    stopAllAudio: () => {
      console.log('🔇 Stopping all audio');
      stopAudio(currentAudioElement, {
        setIsSpeaking,
        setBotSpeechCaption: startTypingAnimation,
        setShowLoader
      });
      setCurrentAudioElement(null);
      startTypingAnimation(""); // Clear typing animation
    },
    
    // WebSocket management (will be updated via useEffect)
    disconnectWebSocket: () => {
      console.log('🔌 Disconnecting WebSocket via context');
      // Will be updated after WebSocket is initialized
    },
    
    // Data storage
    storeEvaluationResult: async (evaluationData) => {
      try {
        console.log('📊 Storing evaluation result:', evaluationData);
        
        // Include the conversation transcript in the evaluation data
        const evaluationDataWithTranscript = {
          ...evaluationData,
          transcript: conversationTranscript
        };
        
        await storeInterviewDataLocally(evaluationDataWithTranscript);
        
        // Also try AWS storage if available
        try {
          await attemptAWSStorage(evaluationDataWithTranscript);
        } catch (awsError) {
          console.warn('⚠️ AWS storage failed, but local storage succeeded:', awsError);
        }
        
        return true;
      } catch (error) {
        console.error('❌ Failed to store evaluation:', error);
        return false;
      }
    }
  }), [
    backendSTTEnabled,
    interviewState.isTerminating,
    interviewState.isTerminated,
    interviewState.isCompleted,
    currentAudioElement,
    currentInterviewSlotId,
    cleanupStoredVideoUrls,
    sendVideoUrlToBackend,
    clearAllRetries,
    createConclusionAcknowledgment,
    recoverFailedVideoUrlSends,
    audioRecording,
    setIsAITalking,
    sessionId,
    hasReceivedInitialResponse,
    setHasReceivedInitialResponse,
    useMediaServer,
    mediaServerRecording,
    setStreamingTranscript,
    setLiveCaption,
    streamingTranscript,
    liveCaption,
    conversationTranscript,
    startTypingAnimation
  ]); // Include audioRecording and setIsAITalking for AI response handling

  // Update message handler context with WebSocket functions after it's initialized
  useEffect(() => {
    // Set global context for websocket message handler
    window.currentMessageHandlerContext = messageHandlerContext;
    
    // Process any queued messages
    if (window.pendingWebSocketMessages && window.pendingWebSocketMessages.length > 0) {
      window.pendingWebSocketMessages.forEach(({ data, event }) => {
        try {
          handleWebSocketMessage(data, messageHandlerContext);
        } catch (error) {
          console.error('❌ Error processing queued message:', error);
        }
      });
      window.pendingWebSocketMessages = [];
    }
    
    if (websocket.sendMessage && websocket.disconnect) {
      messageHandlerContext.sendMessage = websocket.sendMessage;
      messageHandlerContext.disconnectWebSocket = websocket.disconnect;
      
      // Try to recover any failed video URL sends when WebSocket connects
      if (websocket.isConnected) {
        setTimeout(() => {
          recoverFailedVideoUrlSends();
        }, 500);
      }
    }
    
    // Cleanup function
    return () => {
      window.currentMessageHandlerContext = null;
    };
  }, [websocket.sendMessage, websocket.disconnect, websocket.isConnected, recoverFailedVideoUrlSends]); // Remove messageHandlerContext to prevent loops

  // Audio chunk sending is handled directly in the onAudioChunk callback above

  // ============ TRANSCRIPT CONTRAST MANAGEMENT ============

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) {
      return undefined;
    }

    if (captionSamplingFailedRef.current) {
      return undefined;
    }

    const canvas =
      captionSamplingCanvasRef.current || document.createElement("canvas");
    captionSamplingCanvasRef.current = canvas;

    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) {
      return undefined;
    }

    let isMounted = true;

    const sampleFrame = () => {
      if (!isMounted || captionSamplingFailedRef.current) {
        return;
      }

      const element = videoRef.current;
      if (!element || element.readyState < 2 || element.paused || element.ended) {
        return;
      }

      const sampleWidth = Math.min(64, element.videoWidth || 0);
      const sampleHeight = Math.min(36, element.videoHeight || 0);

      if (!sampleWidth || !sampleHeight) {
        return;
      }

      canvas.width = sampleWidth;
      canvas.height = sampleHeight;

      try {
        context.drawImage(element, 0, 0, sampleWidth, sampleHeight);
        const frame = context.getImageData(0, 0, sampleWidth, sampleHeight);
        const { data } = frame;
        if (!data || data.length === 0) {
          return;
        }

        let luminanceSum = 0;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          luminanceSum += 0.2126 * r + 0.7152 * g + 0.0722 * b;
        }

        const averageLuminance = luminanceSum / (data.length / 4);
        const nextTone = averageLuminance > 155 ? "dark" : "light";

        setCaptionTone((currentTone) =>
          currentTone === nextTone ? currentTone : nextTone
        );
      } catch (error) {
        if (!captionSamplingFailedRef.current) {
          captionSamplingFailedRef.current = true;
          setCaptionTone("light");
          console.warn("⚠️ Transcript contrast sampling disabled:", error);
        }
      }
    };

    const intervalId = window.setInterval(sampleFrame, 1000);
    sampleFrame();

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [isVideoOn]);

  // ============ TAB VISIBILITY MANAGEMENT ============
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Don't stop recording when tab is hidden - just log it
        // Speech recognition can continue in background
      } else {
        // Don't restart recording, let it continue naturally
        // Only start if we're not already recording and should be
        if (!audioRecording?.isRecording && !isAITalking && interviewState.isActive) {
          setTimeout(async () => {
            await audioRecording.startRecording();
          }, 500); // Longer delay to avoid rapid cycling
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [audioRecording, isAITalking, interviewState.isActive]);

  // ============ UTILITY FUNCTIONS ============

  const buildPublicS3Url = useCallback((key) => {
    try {
      const bucket = awsExports?.aws_user_files_s3_bucket;
      const region = awsExports?.aws_user_files_s3_bucket_region;
      if (!bucket || !region || !key) return null;
      
      const normalizedKey = key.startsWith("public/") ? key : `public/${key}`;
      return `https://${bucket}.s3.${region}.amazonaws.com/${normalizedKey}`;
    } catch (e) {
      console.warn("Failed to build public S3 URL:", e);
      return null;
    }
  }, []);

  // ============ SECURE WINDOW UTILITIES ============

  // Function to open interview in a secure window (can be called from parent component)
  const openSecureInterviewWindow = useCallback((url) => {
    const secureWindowFeatures = [
      'width=1200',
      'height=800',
      'left=100',
      'top=100',
      'resizable=no',
      'scrollbars=no',
      'toolbar=no',
      'menubar=no',
      'location=no',
      'directories=no',
      'status=no',
      'copyhistory=no'
    ].join(',');

    const secureWindow = window.open(url, 'SecureInterview', secureWindowFeatures);
    
    if (secureWindow) {
      // Focus the new window
      secureWindow.focus();
      
      // Monitor the secure window
      const checkWindow = setInterval(() => {
        if (secureWindow.closed) {
          clearInterval(checkWindow);
          console.log('🔒 Secure interview window was closed');
        }
      }, 1000);
      
      return secureWindow;
    } else {
      console.error('❌ Failed to open secure interview window - popup blocked?');
      toast.error('Please allow popups for this site to open the secure interview window.');
      return null;
    }
  }, []);

  // Function to detect if running in a secure window context
  const isInSecureWindow = useCallback(() => {
    try {
      // Check if window has restricted features
      const hasRestrictedFeatures = (
        !window.toolbar.visible ||
        !window.menubar.visible ||
        !window.locationbar.visible ||
        !window.statusbar.visible
      );
      
      // Check if window was opened by another window
      const isPopup = window.opener !== null;
      
      // Check window size constraints
      const hasConstrainedSize = (
        window.outerWidth <= 1200 &&
        window.outerHeight <= 800
      );
      
      return hasRestrictedFeatures || isPopup || hasConstrainedSize;
    } catch (error) {
      console.warn('⚠️ Could not determine secure window status:', error);
      return false;
    }
  }, []);

  // Function to properly close the interview window
  const closeInterviewWindow = useCallback(() => {
    console.log('🔒 Attempting to close interview window...');
    
    try {
      // Method 1: Direct window.close() attempt (works for popups and some browser contexts)
      window.close();
      console.log('✅ window.close() called');
      
      // Method 2: If window.close() doesn't work immediately, try alternative approaches
      setTimeout(() => {
        if (!window.closed) {
          console.log('🔄 Trying alternative close methods...');
          
          // Try to navigate away (this can trigger tab closure in some browsers)
          try {
            window.location.href = 'about:blank';
          } catch (navError) {
            console.log('⚠️ Navigation method failed:', navError);
          }
          
          // Try to open a new window and close this one
          try {
            const newWindow = window.open('', '_self');
            if (newWindow) {
              newWindow.close();
            }
          } catch (replaceError) {
            console.log('⚠️ Window replacement method failed:', replaceError);
          }
          
          // Final attempt: Use history manipulation
          try {
            if (window.history.length > 1) {
              window.history.back();
            } else {
              // Try to close with opener reference if available
              if (window.opener) {
                window.opener = null;
                window.close();
              }
            }
          } catch (historyError) {
            console.log('⚠️ History navigation failed:', historyError);
          }
        }
      }, 100);
      
      // Final check after all attempts
      setTimeout(() => {
        if (!window.closed) {
          console.log('🔄 All automated close methods attempted');
          // Force a page reload to about:blank as last resort
          try {
            window.location.replace('about:blank');
          } catch (finalError) {
            console.log('⚠️ Final close attempt failed:', finalError);
          }
        }
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error attempting to close window:', error);
      // Even if there's an error, try the basic close method
      try {
        window.close();
      } catch (finalError) {
        console.error('❌ Final window.close() also failed:', finalError);
      }
    }
  }, []);

  // ============ SCREEN RECORDING ============

  const startScreenRecording = useCallback(async () => {
    // This function is now deprecated as screen recording is handled during permission request
    // Keeping for backward compatibility but just logging
    console.log('ℹ️ startScreenRecording called (now handled in requestPermissions)');
  }, [
    screenRecordingInitiated, 
    useMediaServer, 
    mediaServerRecording
  ]);

  // ============ PERMISSION MANAGEMENT ============

  const requestPermissions = useCallback(async () => {
    try {
      console.log('🎥 Requesting camera and microphone permissions...');
      
      // STEP 1: Get camera and microphone (mandatory)
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: audioConstraints
        });

        console.log('✅ Camera and microphone permissions granted');
        
        // Set up video stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setIsVideoOn(true);
        }
      } catch (error) {
        console.error('❌ Camera/Microphone permission denied:', error);
        interviewState.setPermissions({ camera: false, microphone: false });
        toast.error('Camera and Microphone access are required to start the interview. Please grant permissions to continue.');
        return false;
      }

      // Camera and mic granted - set permissions to true so interview can start
      interviewState.setPermissions({ camera: true, microphone: true });

      // Try screen recording in background (non-blocking)
      try {
        if (useMediaServer && mediaServerRecording.isEnabled) {
          if (!mediaServerRecording.isConnected) {
            console.log('🔌 Connecting to media server...');
            const connected = await mediaServerRecording.connect();
            if (connected) {
              console.log('📺 Starting screen recording...');
              await mediaServerRecording.startRecording();
              console.log('✅ Screen recording started');
              setScreenRecordingInitiated(true);
            } else {
              console.warn('⚠️ Media server connection failed, continuing without screen recording');
            }
          } else {
            await mediaServerRecording.startRecording();
            setScreenRecordingInitiated(true);
          }
        }
      } catch (screenError) {
        console.warn('⚠️ Screen recording failed, continuing without it:', screenError.message);
      }

      return true;
      
    } catch (error) {
      console.error('❌ Permission request failed:', error);
      interviewState.setPermissions({ camera: false, microphone: false });
      toast.error('Camera and Microphone access are required to start the interview. Please grant permissions to continue.');
      return false;
    }
  }, [audioConstraints, interviewState, useMediaServer, mediaServerRecording]);

  // ============ INTERVIEW CONTROL ============

  const startInterview = useCallback(async () => {
    // Prevent starting if interview is already terminated or completed
    if (interviewState.isCompleted || interviewState.isTerminated) {
      console.log('🚫 Cannot start interview - already terminated/completed');
      return;
    }

    // Additional safeguard: Check localStorage for completion status
    try {
      const token = window.location.pathname.split("/").pop();
      const isCompletedInStorage = localStorage.getItem(`interview_completed_${token}`) === "true";
      if (isCompletedInStorage) {
        console.log('🚫 Cannot start interview - marked as completed in localStorage');
        interviewState.completeInterview();
        return;
      }
    } catch (error) {
      console.error("Error checking localStorage completion status:", error);
    }

    // Check if all required permissions are granted
    const needsPermissions = !interviewState.permissions.camera || !interviewState.permissions.microphone;
    if (!needsPermissions && !interviewState.canStart) {
      console.warn('⚠️ Cannot start interview in current state:', interviewState.currentState);
      return;
    }

    // Request ALL permissions (camera, microphone, screen recording) - ALL are mandatory
    if (needsPermissions) {
      console.log('🔐 Requesting all required permissions (camera, microphone, screen recording)...');
      const granted = await requestPermissions();
      if (!granted) {
        console.log('❌ Permissions denied - prompting user to grant permissions');
        
        // Show persistent prompt to user
        const retryPermissions = window.confirm(
          'Camera and Microphone permissions are required to start the interview.\n\n' +
          'Please click OK to grant permissions and start the interview.'
        );
        
        if (retryPermissions) {
          // Retry permission request
          console.log('🔄 Retrying permission request...');
          setTimeout(() => startInterview(), 500);
        } else {
          toast.error('Cannot start interview without required permissions. Please grant all permissions to continue.');
        }
        return;
      }
      console.log('✅ All required permissions granted successfully');
    }

    console.log('🎬 Starting interview with all permissions granted');

    // Clear stale localStorage values from previous interviews
    // Only clear if they don't match current interview type
    const currentStoredType = localStorage.getItem('interviewType');
    if (!currentStoredType) {
      // If no explicit type is stored, clear the screening flag to prevent conflicts
      localStorage.removeItem('isScreeningRound');
      console.log('🧹 Cleared stale isScreeningRound flag');
    }

    // Reset video URL states for clean interview start
    setVideoUrlSent(false);
    setPendingVideoUrl(null);
    setVideoUrlRetryCount(0);
    setScreenRecordingUrl(null);
    setMediaServerRecordingUrl(null);
    setMediaServerUploadProgress(0);
    setIsMediaServerUploadComplete(false);
    setIsAwaitingVideoUpload(false);
    setScreenRecordingInitiated(false);

    // Start the interview state FIRST
    const success = interviewState.startInterview();
    if (!success) {
      // Silently handle interview start failure - often resolves on retry
      return;
    }

    // THEN connect WebSocket (this is the key fix - only connect when manually started)
    if (!websocket.isConnected && !websocket.isConnecting) {
      console.log('🔗 Connecting WebSocket for interview');
      websocket.connect();
    } else {
      console.log('🔗 WebSocket already connected or connecting');
    }
    
    // Screen recording is already started during permission request
    // No need to start it again here

    console.log('✅ Interview started successfully with all permissions granted');
  }, [interviewState, requestPermissions, websocket, startScreenRecording]);

  // ============ AUTO-START EFFECT ============
  // Automatically start interview when ready (skip "Ready to Start" dialog)
  useEffect(() => {
    if (interviewState.currentState === 'ready_to_start' && !interviewState.isActive) {
      console.log('🚀 Auto-starting interview (skipping Ready to Start dialog)');
      // Small delay to ensure permissions are fully set up
      const autoStartTimer = setTimeout(() => {
        startInterview();
      }, 500);
      
      return () => clearTimeout(autoStartTimer);
    }
  }, [interviewState.currentState, interviewState.isActive, startInterview]);

  const stopInterview = useCallback(async () => {
    if (!interviewState.canTerminate) {
      return;
    }
    
    // Stop all recording first
    audioRecording.stopRecording();
    
    // Stop media server recording - always call if using media server
    if (useMediaServer) {
      mediaServerRecording.stopRecording(); // This will send recording_stopped message and handle final chunk
    }
    
    // Stop any legacy screen recording reference
    if (screenRecorderRef.current && screenRecorderRef.current.state !== "inactive") {
      try {
        screenRecorderRef.current.stop();
      } catch (err) {
        console.error("Failed to stop legacy screen recording:", err);
      }
    }
    
    // Send interview termination message to main WebSocket
    if (websocket.isConnected) {
      websocket.sendMessage({
        type: 'terminate_interview',
        interview_id: currentInterviewSlotId || 'unknown_slot',
        timestamp: new Date().toISOString(),
        reason: 'user_ended_call'
      });
      
      // Disconnect WebSocket after a brief delay to ensure message is sent
      setTimeout(() => {
        websocket.disconnect();
      }, 200);
    }
    
    // Stop any playing audio
    stopAudio(currentAudioElement, {
      setIsSpeaking,
      setBotSpeechCaption,
      setShowLoader
    });
    
    // Complete interview state directly (skip terminating/CSS loading)
    interviewState.completeInterview();
    
    // Webhook functionality removed
    
    // Note: The UI will automatically show completion dialog when interviewState.isTerminated becomes true
    // No CSS loading or additional processing needed
    
  }, [
    interviewState, 
    audioRecording, 
    useMediaServer, 
    mediaServerRecording, 
    websocket, 
    currentInterviewSlotId, 
    currentAudioElement,
    screenRecorderRef
  ]);

  // ============ MEDIA CONTROLS ============

  const toggleVideo = useCallback(async () => {
    if (interviewState.isTerminated || interviewState.isCompleted) return;

    try {
      if (isVideoOn) {
        // Turn off video
        if (videoRef.current && videoRef.current.srcObject) {
          const tracks = videoRef.current.srcObject.getVideoTracks();
          tracks.forEach(track => track.stop());
        }
        setIsVideoOn(false);
      } else {
        // Turn on video
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setIsVideoOn(true);
      }
    } catch (error) {
      console.error('❌ Failed to toggle video:', error);
      toast.error('Failed to toggle video');
    }
  }, [isVideoOn, interviewState]);

  const toggleMicrophone = useCallback(async () => {
    if (interviewState.isTerminated || interviewState.isCompleted) return;

    // Enhanced microphone control with suspend/resume for AI responses
    if (audioRecording.isRecording) {
      // Check if AI is currently speaking
      if (isAITalking) {
        // Suspend recording during AI response
        console.log('🔇 Suspending recording during AI response');
        audioRecording.suspendRecording();
      } else {
        // Normal stop
        audioRecording.stopRecording();
      }
    } else {
      // Check if we need to resume or start fresh
      if (isAITalking) {
        console.log('⚠️ Cannot start recording while AI is speaking');
        toast.warning('Please wait for the AI to finish speaking');
        return;
      }
      
      // Start fresh recording
      const success = await audioRecording.startRecording();
      if (!success) {
        console.error('❌ Failed to start audio recording');
        toast.error('Failed to start microphone. Please check permissions.');
      }
    }
  }, [audioRecording, interviewState, isAITalking]);

  // ============ DATA LOADING ============

  const extractCandidateEmailFromURL = useCallback(() => {
    // First, check if this is a public interview (access from PublicInterview.js)
    const isPublicInterview = localStorage.getItem("publicInterviewMode") === "true";
    const candidateEmailFromStorage = localStorage.getItem("candidateProfileEmailID");

    if (isPublicInterview && candidateEmailFromStorage) {
      console.log('🔗 Using candidate email from public interview localStorage:', candidateEmailFromStorage);
      return candidateEmailFromStorage;
    }

    // Fallback to URL parameters (for direct interview links)
    const urlParams = new URLSearchParams(window.location.search);
    const emailFromUrl = urlParams.get("candidateEmail") || urlParams.get("email");

    if (emailFromUrl) {
      console.log('🔗 Using candidate email from URL parameters:', emailFromUrl);
      return emailFromUrl;
    }

    // No email found
    console.warn('⚠️ No candidate email found in URL or localStorage');
    return null;
  }, []);

  const extractInterviewSlotIdFromSources = useCallback(() => {
    // Check current state first
    if (currentInterviewSlotId) {
      console.log('🎯 Using interview slot ID from current state:', currentInterviewSlotId);
      return currentInterviewSlotId;
    }

    // Check localStorage for various possible keys
    const possibleKeys = [
      'interviewSlotId',
      'currentInterviewSlotId', 
      'interview_slot_id',
      'slotId',
      'publicInterviewSlotID'
    ];

    for (const key of possibleKeys) {
      const slotId = localStorage.getItem(key);
      if (slotId) {
        console.log(`🎯 Using interview slot ID from localStorage[${key}]:`, slotId);
        return slotId;
      }
    }

    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const possibleParams = ['slotId', 'interviewSlotId', 'interview_slot_id', 'slot'];
    
    for (const param of possibleParams) {
      const slotId = urlParams.get(param);
      if (slotId) {
        console.log(`🎯 Using interview slot ID from URL param[${param}]:`, slotId);
        return slotId;
      }
    }

    // Check interview slot data if available
    if (interviewSlotData?.id) {
      console.log('🎯 Using interview slot ID from loaded slot data:', interviewSlotData.id);
      return interviewSlotData.id;
    }

    console.warn('⚠️ No interview slot ID found in any source');
    return null;
  }, [currentInterviewSlotId, interviewSlotData?.id]);

  const fetchCandidateData = useCallback(async (email) => {
    try {
      const candidateResult = await API.graphql({
        query: listCandidateProfiles,
        variables: { filter: { email: { eq: email } } }
      });
      return candidateResult.data.listCandidateProfiles.items[0] || null;
    } catch (error) {
      console.error('❌ Failed to fetch candidate data:', error);
      return null;
    }
  }, []);

  const fetchInterviewSlotData = useCallback(async (email) => {
    try {
      const slotResult = await API.graphql({
        query: listInterviewSlots,
        variables: { filter: { candidateEmail: { eq: email } } }
      });
      
      const slots = slotResult.data.listInterviewSlots.items;
      return slots.length > 0 ? slots[0] : null;
    } catch (error) {
      console.error('❌ Failed to fetch interview slot data:', error);
      return null;
    }
  }, []);

  const fetchJobData = useCallback(async (jobId) => {
    try {
      const jobResult = await API.graphql({
        query: getCompanyJobDescription,
        variables: { id: jobId }
      });
      return jobResult.data.getCompanyJobDescription;
    } catch (error) {
      console.error('❌ Failed to fetch job data:', error);
      return null;
    }
  }, []);

  const loadInterviewData = useCallback(async () => {
    setIsDataLoading(true);
    setDataLoadError(null);

    try {
      // Check if we're in public interview mode with pre-loaded data
      const isPublicInterview = localStorage.getItem("publicInterviewMode") === "true";
      
      if (isPublicInterview) {
        // Get pre-loaded data from PublicInterview.js
        const candidateDataStr = localStorage.getItem('publicInterviewCandidateData');
        const slotDataStr = localStorage.getItem('publicInterviewSlotData');
        const candidateEmail = localStorage.getItem('candidateProfileEmailID');
        const interviewSlotId = localStorage.getItem('interviewSlotID');
        
        if (candidateDataStr && slotDataStr && candidateEmail) {
          try {
            const candidateData = JSON.parse(candidateDataStr);
            const slotData = JSON.parse(slotDataStr);
            
            setCandidateEmail(candidateEmail);
            setCandidateData(candidateData);
            setInterviewSlotData(slotData);
            setCurrentInterviewSlotId(interviewSlotId || slotData.id);
            
            // Check if this is a demo interview and disable media server recording
            const isDemo = candidateEmail && candidateEmail.includes('@demo.getscreened.ai');
            if (isDemo) {
              console.log('🎭 Demo interview detected - Media server recording disabled');
              setIsDemoInterview(true);
              setUseMediaServer(false);
            }
            
            // Load job data if available
            if (slotData.jdID) {
              const job = await fetchJobData(slotData.jdID);
              if (job) {
                setJobData(job);
              }
            }
            
            setIsDataLoading(false);
            setIsValidating(false);
            return;
            
          } catch (parseError) {
            console.error('❌ Failed to parse stored interview data:', parseError);
            // Fall through to normal loading
          }
        }
      }

      // Regular data loading (when not in public interview mode)
      const email = extractCandidateEmailFromURL();
      if (!email) {
        throw new Error('No candidate email found in URL or localStorage');
      }

      setCandidateEmail(email);

      // Load candidate data
      const candidate = await fetchCandidateData(email);
      if (candidate) {
        setCandidateData(candidate);
      }

      // Load interview slot data
      const interviewSlot = await fetchInterviewSlotData(email);
      if (interviewSlot) {
        setInterviewSlotData(interviewSlot);
        setCurrentInterviewSlotId(interviewSlot.id);

        // Load job data
        if (interviewSlot.jdID) {
          const job = await fetchJobData(interviewSlot.jdID);
          if (job) {
            setJobData(job);
          }
        }
      }

    } catch (error) {
      console.error('❌ Failed to load interview data:', error);
      setDataLoadError(error.message);
    } finally {
      setIsDataLoading(false);
      setIsValidating(false);
    }
  }, []); // Make stable to prevent loops

  // ============ EFFECTS ============

  // Load data on mount
  useEffect(() => {
    loadInterviewData();
  }, []); // Remove loadInterviewData from dependencies to prevent loops

  // Check initial permissions on mount to transition from INITIALIZING state
  useEffect(() => {
    const checkInitialPermissions = async () => {      
      try {
        // Always start with false permissions to force the permission dialog
        interviewState.setPermissions({ camera: false, microphone: false });
        return;
        
        // The code below is commented out to force the permission dialog
        // Uncomment if you want to auto-detect permissions
        /*
        // First, just check if permissions API is supported
        if (!navigator.permissions) {
          console.log('🔍 Permissions API not supported, requiring manual permission request');
          interviewState.setPermissions({ camera: false, microphone: false });
          return;
        }

        // Check current permission states
        const cameraPermission = await navigator.permissions.query({ name: 'camera' });
        const microphonePermission = await navigator.permissions.query({ name: 'microphone' });
        
        const hasCamera = cameraPermission.state === 'granted';
        const hasMicrophone = microphonePermission.state === 'granted';
        
        console.log('🔍 Current permissions:', { 
          camera: cameraPermission.state, 
          microphone: microphonePermission.state 
        });
        
        // Only set to true if explicitly granted, otherwise require user interaction
        interviewState.setPermissions({ 
          camera: hasCamera, 
          microphone: hasMicrophone 
        });
        
        // If both permissions are already granted, we can set up the video stream
        if (hasCamera && hasMicrophone) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: audioConstraints
            });
            
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              setIsVideoOn(true);
            }
          } catch (error) {
            console.warn('⚠️ Failed to get media stream despite granted permissions:', error);
            // Reset permissions if we can't actually use them
            interviewState.setPermissions({ camera: false, microphone: false });
          }
        }
        */
      } catch (error) {
        // If checking fails, default to requiring permission request
        interviewState.setPermissions({ camera: false, microphone: false });
      }
    };

    // Only check permissions once when component mounts and interview state is initializing
    if (interviewState.isInitializing) {
      checkInitialPermissions();
    }
  }, [interviewState.isInitializing, interviewState.setPermissions, audioConstraints]);

  // Send interview start message when WebSocket connects during active interview
  useEffect(() => {
    // Only run if conditions are met and message hasn't been sent yet
    if (interviewState.isActive && websocket.isConnected && !startInterviewSent) {
      // Extract candidate email with fallback mechanisms
      const fallbackCandidateEmail = candidateData?.email || candidateEmail || extractCandidateEmailFromURL();
      
      // Extract company email with fallback
      const fallbackCompanyEmail = jobData?.company_email || localStorage.getItem('companyEmail') || '';
      
      // Extract interview slot ID with fallback
      const fallbackInterviewSlotId = extractInterviewSlotIdFromSources();
      
      // Only require candidate email and some form of interview identifier
      if (fallbackCandidateEmail && fallbackInterviewSlotId) {
        console.log('🚀 Sending START_INTERVIEW message to server');
        console.log(`📧 Email: ${fallbackCandidateEmail}, 🆔 Slot: ${fallbackInterviewSlotId}`);
        
        // Extract candidate profile ID with fallback mechanisms
        const fallbackCandidateProfileId = candidateData?.id || localStorage.getItem('candidateProfileID') || interviewSlotData?.candidateProfileID;
        
        // Extract numberOfQuestions from localStorage (set by PublicInterview.js)
        const storedQuestions = localStorage.getItem('interviewNumberOfQuestions');
        const numberOfQuestions = storedQuestions ? parseInt(storedQuestions, 10) : 5;
        const validQuestions = (numberOfQuestions > 0 && numberOfQuestions <= 20) ? numberOfQuestions : 5;
        
        // Extract meetingConfig for candidate-initiated interview flags
        let meetingConfig = null;
        try {
          const slotDataStr = localStorage.getItem('publicInterviewSlotData');
          if (slotDataStr) {
            const slotData = JSON.parse(slotDataStr);
            if (slotData.meetingConfig) {
              meetingConfig = typeof slotData.meetingConfig === 'string' 
                ? JSON.parse(slotData.meetingConfig) 
                : slotData.meetingConfig;
            }
          }
        } catch (parseErr) {
          console.warn('⚠️ Could not parse meetingConfig:', parseErr);
        }
        
        // Extract candidate-initiated flags
        const isCandidateInitiated = meetingConfig?.isCandidateInitiated || meetingConfig?.interviewType === 'candidate-initiated';
        const customRoleData = meetingConfig?.customRole || null;
        
        // Check if this is a screening round
        const isScreeningInterview = meetingConfig?.isScreeningRound === true || 
                                     meetingConfig?.interviewType === 'screening' ||
                                     localStorage.getItem('isScreeningRound') === 'true';
        
        // Determine interview type and store it
        const determinedInterviewType = isScreeningInterview ? 'screening' : 'jd_specific';
        setInterviewType(determinedInterviewType);
        localStorage.setItem('interviewType', determinedInterviewType);
        
        // Extract resume data for screening round
        let candidateResumeData = null;
        if (isScreeningInterview && candidateData) {
          console.log('📋 Screening round detected - extracting resume data...');
          console.log('📋 Candidate data for screening:', {
            fullName: candidateData?.fullName,
            roleApplyFor: candidateData?.roleApplyFor,
            keySkills: candidateData?.keySkills?.substring(0, 50) + '...',
            hasData: !!candidateData
          });
          candidateResumeData = extractResumeDataForScreening(candidateData);
          console.log('📋 Extracted target_role:', candidateResumeData?.roleApplyFor);
        } else if (isScreeningInterview && fallbackCandidateProfileId) {
          // If candidateData not available, try to get from meetingConfig or localStorage
          try {
            // Attempt to get from meetingConfig (stored during slot creation)
            const resumeFromConfig = {
              fullName: meetingConfig?.candidateName || '',
              roleApplyFor: meetingConfig?.candidateRole || '',
              keySkills: meetingConfig?.candidateSkills || '',
              workStatus: meetingConfig?.candidateExperience || '',
            };
            if (resumeFromConfig.fullName || resumeFromConfig.roleApplyFor) {
              candidateResumeData = extractResumeDataForScreening(resumeFromConfig);
            }
          } catch (resumeErr) {
            console.warn('⚠️ Could not extract resume data for screening:', resumeErr);
          }
        }
        
        console.log(`📝 Sending to backend - Profile ID: ${fallbackCandidateProfileId}, Interview Type: ${determinedInterviewType}, Candidate Initiated: ${isCandidateInitiated}, Screening: ${isScreeningInterview}`);
        
        const currentTime = Date.now();
        setLastConnectionTime(currentTime);
        setConnectionAttempts(prev => prev + 1);
        
        const startMessage = {
          type: MESSAGE_TYPES.START_INTERVIEW,
          interview_id: fallbackInterviewSlotId,
          interview_slot_id: fallbackInterviewSlotId,
          session_id: sessionId,
          connection_attempt: connectionAttempts + 1,
          connection_time: currentTime,
          is_reconnection: hasReceivedInitialResponse,
          candidate_email: fallbackCandidateEmail,
          candidate_profile_id: fallbackCandidateProfileId,
          company_email: fallbackCompanyEmail,
          interviewType: determinedInterviewType, // 'screening' (20 min) or 'jd_specific' (30 min)
          is_demo_interview: isDemoInterview,
          // Candidate-initiated interview flags
          is_candidate_initiated: isCandidateInitiated,
          interview_type: determinedInterviewType, // Legacy field for backward compatibility
          // Custom role data (if candidate created their own role)
          custom_role: customRoleData ? {
            job_title: customRoleData.jobTitle,
            skills: customRoleData.skills,
            experience: customRoleData.experience,
            description: customRoleData.description
          } : null,
          // Screening round specific fields
          is_screening_round: isScreeningInterview,
          candidate_resume_data: isScreeningInterview && candidateResumeData ? {
            // Personal Info
            full_name: candidateResumeData.fullName || '',
            email: candidateResumeData.email || fallbackCandidateEmail,
            phone: candidateResumeData.mobileNumber || '',
            location: candidateResumeData.currentCity || candidateResumeData.address || '',
            
            // Professional Info
            target_role: candidateResumeData.roleApplyFor || '',
            experience_status: candidateResumeData.workStatus || '',
            experience_level: candidateResumeData.experienceLevel || 'unknown',
            
            // Skills and Background
            skills: candidateResumeData.keySkillsArray || [],
            skills_string: candidateResumeData.keySkills || '',
            description: candidateResumeData.description || '',
            education: candidateResumeData.educationDetails || '',
            
            // Work Preferences
            work_mode: candidateResumeData.workMode || [],
            work_category: candidateResumeData.workCategory || [],
            salary_expectation: candidateResumeData.preferredSalary || '',
            
            // Links
            linkedin_url: candidateResumeData.linkedinURL || '',
            github_url: candidateResumeData.githubURL || '',
            resume_url: candidateResumeData.resumeURL || '',
            
            // Additional context
            candidate_goal: candidateResumeData.candidateGoal || '',
          } : null,
          // Screening instructions for the backend AI
          screening_instructions: isScreeningInterview ? {
            focus_areas: [
              'Verify the candidate\'s claimed skills and experience',
              'Assess communication skills',
              'Understand career goals and motivations',
              'Evaluate basic technical knowledge based on stated skills',
              'Check for consistency between resume claims and verbal responses'
            ],
            interview_style: 'conversational',
            question_types: ['behavioral', 'situational', 'skill-verification', 'career-goals']
          } : null
        };
        
        // Debug: Log the full start message to verify role data is included
        console.log('🚀 START_INTERVIEW message being sent:', {
          interview_id: startMessage.interview_id,
          is_screening_round: startMessage.is_screening_round,
          interview_type: startMessage.interview_type,
          has_candidate_resume_data: !!startMessage.candidate_resume_data,
          target_role: startMessage.candidate_resume_data?.target_role || 'NOT SET',
          full_name: startMessage.candidate_resume_data?.full_name || 'NOT SET',
          skills_count: startMessage.candidate_resume_data?.skills?.length || 0,
          custom_role: startMessage.custom_role
        });

        try {
          const messageSent = websocket.sendMessage(startMessage);
          if (messageSent) {
            console.log('✅ START_INTERVIEW message sent successfully');
            setStartInterviewSent(true); // Prevent duplicate sends
          } else {
            console.warn('⚠️ START_INTERVIEW message queued (will retry when connected)');
          }
        } catch (error) {
          console.error('❌ Failed to send START_INTERVIEW message:', error);
        }
      } else {
        console.log('❌ Cannot send start_interview - missing required data:', {
          email: !!fallbackCandidateEmail,
          slotId: !!fallbackInterviewSlotId
        });
      }
    }
  }, [interviewState.isActive, websocket.isConnected, startInterviewSent]); // Minimal dependencies

  // Reset the flag when interview becomes inactive (for potential restarts)
  useEffect(() => {
    if (!interviewState.isActive) {
      setStartInterviewSent(false);
    }
  }, [interviewState.isActive]);

  // Update timer when interview type changes (before interview starts)
  useEffect(() => {
    if (!interviewState.isActive && !interviewState.isStarted) {
      const newDuration = calculateInterviewDuration();
      if (interviewState.timeRemaining !== newDuration) {
        console.log(`⏱️ Updating timer for ${interviewType} interview: ${newDuration / 60} minutes`);
        interviewState.setTimeRemaining(newDuration);
      }
    }
  }, [interviewType, interviewState.isActive, interviewState.isStarted, interviewState.timeRemaining, calculateInterviewDuration, interviewState]);

  // Note: Microphone recording will auto-start only after AI finishes speaking its initial greeting
  // This is handled in the onAudioEnd callback within the playAudio function

  // Monitor media server recording completion and manage video URL sending
  useEffect(() => {
    if (mediaServerRecordingUrl && !videoUrlSent) {
      console.log('🎥 Media server recording completed, video URL ready:', mediaServerRecordingUrl);
      
      // Don't send immediately - wait for conclusion unless interview is terminating abnormally
      if (interviewState.isTerminating && !isConclusionAudioPlaying) {
        console.log('🎥 Interview terminating without conclusion, sending video URL now');
        sendVideoUrlToBackend(mediaServerRecordingUrl, currentInterviewSlotId);
      } else {
        setPendingVideoUrl(mediaServerRecordingUrl);
        console.log('🎥 Video URL ready, waiting for conclusion or termination');
      }
    }
  }, [
    mediaServerRecordingUrl,
    videoUrlSent,
    interviewState.isTerminating,
    isConclusionAudioPlaying,
    sendVideoUrlToBackend,
    currentInterviewSlotId,
    setPendingVideoUrl
  ]);

  // Periodic retry mechanism for pending video URLs
  useEffect(() => {
    if (!pendingVideoUrl || videoUrlSent || interviewState.isCompleted || interviewState.isTerminated) {
      return;
    }

    const intervalId = setInterval(() => {
      if (websocket?.isConnected && pendingVideoUrl && !videoUrlSent) {
        console.log('🔄 Retrying pending video URL send');
        sendVideoUrlToBackend(pendingVideoUrl, currentInterviewSlotId, 1);
      }
    }, 10000); // Retry every 10 seconds

    return () => clearInterval(intervalId);
  }, [
    pendingVideoUrl,
    videoUrlSent,
    interviewState.isCompleted,
    interviewState.isTerminated,
    websocket?.isConnected,
    sendVideoUrlToBackend,
    currentInterviewSlotId
  ]);

  // ============ INTERVIEW COMPLETION WEBHOOK MONITORING ============
  
  // Webhook monitoring removed per security requirements
  useEffect(() => {
    // Webhook functionality removed
  }, [
    interviewState.isCompleted, 
    interviewState.isStarted
  ]);

  // Webhook recovery removed per security requirements

  // ============ SECURITY IMPLEMENTATION ============
  
  // Security useEffect - Implement secure window features
  useEffect(() => {
    // Only activate security when interview is active and not completed/terminated
    if (
      !interviewState.isActive ||
      interviewState.isCompleted ||
      interviewState.isTerminated ||
      securityViolationDetected
    ) {
      return;
    }

    setIsSecurityActive(true);

    // Block right-click context menu
    let lastRightClickWarning = 0;
    const blockRightClick = (e) => {
      e.preventDefault();
      // Reduce logging frequency - only log once per 10 seconds
      const now = Date.now();
      if (now - lastRightClickWarning > 10000) {
        console.warn("🚫 Right-click blocked for security reasons");
        lastRightClickWarning = now;
      }
      toast.warning(
        "Right-click is disabled during the interview for security reasons.",
        {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    };

    // Block developer tools shortcuts and copy/paste
    const blockKeys = (e) => {
      // Block F12, Ctrl+Shift+I/J/C, Ctrl+U, Ctrl+C, Ctrl+V, Ctrl+A, Ctrl+S, Alt+F12
      const blockedKeys = [
        // Developer tools
        { key: "F12" },
        { key: "F12", alt: true },
        { key: "I", ctrl: true, shift: true },
        { key: "J", ctrl: true, shift: true },
        { key: "C", ctrl: true, shift: true },
        { key: "U", ctrl: true },
        // Copy/Paste/Select/Save
        { key: "C", ctrl: true },
        { key: "V", ctrl: true },
        { key: "A", ctrl: true },
        { key: "S", ctrl: true },
        { key: "P", ctrl: true },
        // Additional shortcuts
        { key: "R", ctrl: true }, // Refresh
        { key: "F5" }, // Refresh
      ];

      const isBlocked = blockedKeys.some(blocked => {
        if (blocked.key === e.key || blocked.key === e.key.toUpperCase()) {
          if (blocked.ctrl && !e.ctrlKey) return false;
          if (blocked.shift && !e.shiftKey) return false;
          if (blocked.alt && !e.altKey) return false;
          return true;
        }
        return false;
      });

      if (isBlocked) {
        e.preventDefault();
        e.stopPropagation();
        console.warn(`🚫 Blocked key combination: ${e.key} (Ctrl: ${e.ctrlKey}, Shift: ${e.shiftKey}, Alt: ${e.altKey})`);
        
        toast.warning(
          "This keyboard shortcut is not allowed during the interview for security reasons.",
          {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      }
    };

    // Store baseline measurements to avoid false positives from screen recording dialogs
    let baselineWidth = window.outerWidth - window.innerWidth;
    let baselineHeight = window.outerHeight - window.innerHeight;
    let measurementStabilized = false;
    
    // Use a global flag to prevent multiple triggers across effect re-runs
    if (!window.securityTriggered) {
      window.securityTriggered = false;
    }

    // Wait for screen recording dialog to settle before taking baseline
    setTimeout(() => {
      baselineWidth = window.outerWidth - window.innerWidth;
      baselineHeight = window.outerHeight - window.innerHeight;
      measurementStabilized = true;
    }, 5000); // Wait 5 seconds for screen recording setup to complete

    // Enhanced developer tools detection enabled
    console.log('� Developer tools detection enabled for security');
    
    // Enhanced developer tools detection function - DISABLED
    function detectDevTools() {
      // DISABLED - This function was interfering with console logging
      // The original detection logic has been commented out to allow proper debugging
      console.log('� DevTools detection called but disabled');
    }

    // Disable console methods (optional - can be commented out for development)
    const originalConsole = {};
    const disableConsole = true; // Set to true to disable console in production
    
    if (disableConsole) {
      for (const method of ["log", "debug", "warn", "info", "error"]) {
        originalConsole[method] = console[method];
        console[method] = () => {};
      }
    }

    // Initial detection
    const initialDetectionTimeout = setTimeout(() => {
      detectDevTools();
    }, 8000);

    // Set up resize observer for monitoring
    let resizeObserver = null;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(entries => {
        detectDevTools();
      });
      resizeObserver.observe(document.body);
    }
    
    // Set up interval for periodic checks
    const interval = setInterval(() => {
      detectDevTools();
    }, 3000); // Check every 3 seconds

    // Add event listeners
    document.addEventListener("contextmenu", blockRightClick, { passive: false });
    document.addEventListener("keydown", blockKeys, { passive: false });

    // Prevent text selection during interview
    const blockSelection = (e) => {
      e.preventDefault();
      return false;
    };
    
    document.addEventListener("selectstart", blockSelection);
    document.addEventListener("dragstart", blockSelection);

    // Block print functionality
    const blockPrint = (e) => {
      e.preventDefault();
      toast.warning("Printing is not allowed during the interview.", {
        position: "top-center",
        autoClose: 3000,
      });
    };

    window.addEventListener("beforeprint", blockPrint);

    // Additional security measures
    
    // Disable drag and drop
    const blockDragDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    document.addEventListener("dragover", blockDragDrop);
    document.addEventListener("drop", blockDragDrop);

    // Monitor for suspicious activities
    let suspiciousActivityCount = 0;
    const maxSuspiciousActivities = 5;

    const monitorSuspiciousActivity = (activityType) => {
      suspiciousActivityCount++;
      console.warn(`🚨 Suspicious activity detected: ${activityType} (${suspiciousActivityCount}/${maxSuspiciousActivities})`);
      
      // DISABLED: Log the suspicious activity
      // handleSecurityViolation(SECURITY_EVENTS.SUSPICIOUS_ACTIVITY, {
      //   activityType,
      //   count: suspiciousActivityCount,
      //   timestamp: new Date().toISOString()
      // });
      
      if (suspiciousActivityCount >= maxSuspiciousActivities) {
        setSecurityViolationDetected(true);
        
        // DISABLED: Handle as unauthorized action
        // handleSecurityViolation(SECURITY_EVENTS.UNAUTHORIZED_ACTION, {
        //   reason: 'multiple_suspicious_activities',
        //   count: suspiciousActivityCount,
        //   lastActivity: activityType
        // });
        
        toast.error("Multiple security violations detected. Session terminated.", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          toastId: "multiple-violations",
        });
      } else {
        toast.warning(`Security warning ${suspiciousActivityCount}/${maxSuspiciousActivities}: ${activityType}`, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    };

    // Monitor for rapid key combinations (potential automation)
    let lastKeyTime = 0;
    let rapidKeyCount = 0;

    const monitorKeyTiming = (e) => {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime;
      
      if (timeDiff < 50) { // Less than 50ms between keys
        rapidKeyCount++;
        if (rapidKeyCount > 10) {
          monitorSuspiciousActivity("Rapid key input detected");
          rapidKeyCount = 0;
        }
      } else {
        rapidKeyCount = 0;
      }
      
      lastKeyTime = currentTime;
    };

    document.addEventListener("keydown", monitorKeyTiming);

    // Monitor for mouse automation patterns
    let lastMouseTime = 0;
    let rapidMouseCount = 0;

    const monitorMouseTiming = (e) => {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastMouseTime;
      
      if (timeDiff < 10) { // Less than 10ms between mouse events
        rapidMouseCount++;
        if (rapidMouseCount > 20) {
          monitorSuspiciousActivity("Automated mouse activity detected");
          rapidMouseCount = 0;
        }
      } else {
        rapidMouseCount = 0;
      }
      
      lastMouseTime = currentTime;
    };

    document.addEventListener("mousemove", monitorMouseTiming);

    // Prevent common bypass techniques
    
    // Monitor for extension injection attempts
    const originalCreateElement = document.createElement;
    document.createElement = function(tagName) {
      const element = originalCreateElement.call(this, tagName);
      
      if (tagName.toLowerCase() === 'script') {
        console.warn("🚨 Script element creation detected");
        monitorSuspiciousActivity("Script injection attempt");
      }
      
      return element;
    };

    // Block common developer console access patterns
    const blockConsoleAccess = () => {
      if (window.console && typeof window.console.clear === 'function') {
        const originalClear = window.console.clear;
        window.console.clear = function() {
          monitorSuspiciousActivity("Console clear attempt");
          return originalClear.apply(this, arguments);
        };
      }
    };

    blockConsoleAccess();

    // Cleanup function
    return () => {
      setIsSecurityActive(false);

      // Restore console methods
      if (disableConsole) {
        for (const method of ["log", "debug", "warn", "info", "error"]) {
          console[method] = originalConsole[method];
        }
      }

      // Remove event listeners
      document.removeEventListener("contextmenu", blockRightClick);
      document.removeEventListener("keydown", blockKeys);
      document.removeEventListener("selectstart", blockSelection);
      document.removeEventListener("dragstart", blockSelection);
      document.removeEventListener("dragover", blockDragDrop);
      document.removeEventListener("drop", blockDragDrop);
      document.removeEventListener("keydown", monitorKeyTiming);
      document.removeEventListener("mousemove", monitorMouseTiming);
      window.removeEventListener("beforeprint", blockPrint);

      // Restore original createElement
      document.createElement = originalCreateElement;

      // Clear intervals and timeouts
      clearInterval(interval);
      clearTimeout(initialDetectionTimeout);

      // Disconnect resize observer
      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      // Reset global security flag
      window.securityTriggered = false;
    };
  }, [
    interviewState.isActive,
    interviewState.isCompleted,
    interviewState.isTerminated,
    interviewState.canTerminate,
    interviewState.terminateInterview,
    securityViolationDetected,
    audioRecording,
    currentAudioElement,
    websocket,
    currentInterviewSlotId
  ]);

  // ============ TAB VISIBILITY AND FOCUS MONITORING ============
  
  // Monitor tab visibility and focus for additional security
  useEffect(() => {
    if (!interviewState.isActive || securityViolationDetected) {
      return;
    }

    let tabSwitchCount = 0;
    const maxTabSwitches = 3; // Allow up to 3 tab switches before warning

    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabSwitchCount++;
        
        if (tabSwitchCount >= maxTabSwitches) {
          toast.error(
            `You have switched tabs ${tabSwitchCount} times. Please stay focused on the interview.`,
            {
              position: "top-center",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
        } else {
          toast.warning(
            `Please stay on the interview tab. Warning ${tabSwitchCount}/${maxTabSwitches}`,
            {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
        }
      }
    };

    const handleFocusLoss = () => {
      // Window lost focus
    };

    const handleFocusGain = () => {
      // Window gained focus
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleFocusLoss);
    window.addEventListener('focus', handleFocusGain);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleFocusLoss);
      window.removeEventListener('focus', handleFocusGain);
    };
  }, [interviewState.isActive, securityViolationDetected]);

  // ============ BROWSER CLOSURE PROTECTION ============
  
  // Webhook via beacon removed per security requirements

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Webhook removed per security requirements
      
      // Stop all recording (only if active)
      try {
        if (audioRecording && audioRecording.isRecording) {
          audioRecording.stopRecording();
        }
      } catch (error) {
        console.error('❌ Error stopping audio recording during cleanup:', error);
      }
      
      // Stop screen recording
      if (screenRecorderRef.current && screenRecorderRef.current.state !== "inactive") {
        try {
          screenRecorderRef.current.stop();
        } catch (err) {
          console.error("🎥 Failed to stop screen recording during cleanup:", err);
        }
      }
      
      // Stop video stream
      if (videoRef.current && videoRef.current.srcObject) {
        try {
          videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        } catch (err) {
          console.error("📹 Failed to stop video stream during cleanup:", err);
        }
      }
      
      // Disconnect WebSocket
      websocket.disconnect();
      
      // Stop any playing audio
      stopAudio(currentAudioElement, {
        setIsSpeaking,
        setBotSpeechCaption,
        setShowLoader
      });

      // Clear all retries and timeouts
      clearAllRetries();

      // Reset acknowledgment flags
      window.conclusionAcknowledgmentInProgress = false;
      window.interviewCompletedNoMoreRetries = false;

      // Clear any remaining timeouts
      if (window.terminationTimeoutId) {
        clearTimeout(window.terminationTimeoutId);
        window.terminationTimeoutId = null;
      }

      if (backendSTTFallbackTimerRef.current) {
        clearTimeout(backendSTTFallbackTimerRef.current);
        backendSTTFallbackTimerRef.current = null;
      }
    };
  }, []); // Empty dependencies - only run on mount/unmount

  // ============ AI WAVE ANIMATION ============
  
  // Initialize SineWaves animation
  useEffect(() => {
    if (!aiWaveCanvasRef.current || sineWavesInstanceRef.current) {
      return;
    }

    try {
      console.log('🎵 Initializing AI wave animation');
      
      const waves = new SineWaves({
        el: aiWaveCanvasRef.current,
        speed: 5,
        ease: 'SineInOut',
        wavesWidth: '75%',
        waves: [
          {
            timeModifier: 4,
            lineWidth: 1,
            amplitude: -25,
            wavelength: 25
          },
          {
            timeModifier: 2,
            lineWidth: 1,
            amplitude: -10,
            wavelength: 30
          },
          {
            timeModifier: 1,
            lineWidth: 1,
            amplitude: -30,
            wavelength: 30
          },
          {
            timeModifier: 3,
            lineWidth: 1,
            amplitude: 40,
            wavelength: 40
          },
          {
            timeModifier: 0.5,
            lineWidth: 1,
            amplitude: -60,
            wavelength: 60
          },
          {
            timeModifier: 1.3,
            lineWidth: 1,
            amplitude: -40,
            wavelength: 40
          }
        ],
        resizeEvent: function() {
          const gradient = this.ctx.createLinearGradient(0, 0, this.width, 0);
          gradient.addColorStop(0, "rgba(139, 92, 246, 0)");
          gradient.addColorStop(0.5, "rgba(139, 92, 246, 0.75)");
          gradient.addColorStop(1, "rgba(59, 130, 246, 0)");
          
          let index = -1;
          const length = this.waves.length;
          while(++index < length){
            this.waves[index].strokeStyle = gradient;
          }
        }
      });

      sineWavesInstanceRef.current = waves;
      console.log('✅ AI wave animation initialized');
    } catch (error) {
      console.error('❌ Failed to initialize AI wave animation:', error);
    }

    return () => {
      // Cleanup
      if (sineWavesInstanceRef.current) {
        console.log('🧹 Cleaning up AI wave animation');
        sineWavesInstanceRef.current = null;
      }
    };
  }, [interviewState.isActive]); // Initialize when interview becomes active

  // ============ RENDER CONDITIONS ============

  const userSpeakingText =
    streamingTranscript ||
    liveCaption ||
    audioRecording?.currentTranscript ||
    audioRecording?.streamingTranscript ||
    audioRecording?.liveCaption ||
    "";

  const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const latestAIMessage = messages.length > 0 
    ? [...messages].reverse().find(msg => msg.type === "ai") 
    : null;

  const transcriptOverlayText = (() => {
    // Show AI question while it's speaking
    if (isSpeaking) {
      return typingText || fullTypingText || botSpeechCaption || latestAIMessage?.text || "";
    }

    // Always keep showing the last AI question, even when candidate is speaking
    // The question should persist until a new AI question arrives
    // This ensures the candidate always sees what question they need to answer
    return latestAIMessage?.text || "";
  })();

  const sanitizedOverlayText = transcriptOverlayText.trim();

  // Show loading state
  if (isDataLoading || isValidating) {
    return (
      <div className="app-container centered">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h2>
            {isValidating
              ? "Validating Interview Access..."
              : "Loading Interview Data..."}
          </h2>
          <p>
            {isValidating
              ? "Checking interview link validity..."
              : "Fetching candidate and job information..."}
          </p>
        </div>
      </div>
    );
  }

  // Show validation error
  if (validationError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-purple-900 to-indigo-900">
        <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-screen">
          <div className="max-w-md mx-auto bg-white rounded-xl shadow-2xl p-8 text-center">
            <div className="text-red-500 mb-6">
              <svg
                className="mx-auto h-20 w-20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-8">{validationError}</p>
            <p className="text-gray-500 text-sm">
              Please contact the company that sent you this link for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show data loading error
  if (dataLoadError) {
    const isInterviewCompletedError = dataLoadError.includes(
      "already been completed"
    );

    return (
      <div className="app-container centered">
        <div className="error-container">
          <h2>
            {isInterviewCompletedError
              ? "Interview Already Completed"
              : "Failed to Load Interview Data"}
          </h2>
          <p>{dataLoadError}</p>
          {!isInterviewCompletedError && (
            <button
              onClick={() => {
                setDataLoadError(null);
                loadInterviewData();
              }}
              className="retry-button"
            >
              Retry Loading Data
            </button>
          )}
        </div>
      </div>
    );
  }

  // ============ MAIN RENDER ============

  return (
    <>
      {/* Termination Overlay */}
      {interviewState.isTerminating && (
        <div className="termination-overlay">
          <div className="termination-content">
            <div className="termination-spinner"></div>
            <h2 className="termination-title">Finalizing Your Interview</h2>
            <p className="termination-message">
              We are processing your interview responses and uploading your session recording.
              <br />
              This ensures all your data is properly saved and evaluated.
            </p>
            <div className="termination-status">
              {loaderText || "Preparing to finalize interview..."}
            </div>
            <div className="termination-warning">
              ⚠️ Please do not close this window until the process is complete
            </div>
            <div className="termination-progress">
              <div
                className="termination-progress-bar"
                style={{
                  width: isMediaServerUploadComplete ? "100%" : `${Math.max(10, mediaServerUploadProgress)}%`,
                  transition: "width 0.5s ease-out",
                }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Screen */}
      {interviewState.isCompleted ? (
        <div className="app-container centered">
          <div className="completion-card">
            <div className="logo-container">
              <img src={logo} alt="Company Logo" />
            </div>
            <div className="completion-content">
              <div className="success-icon">
                <div className="success-checkmark">✓</div>
              </div>
              <h1>Interview Completed Successfully!</h1>
              <div className="completion-message">
                <p>Thank you for completing your interview session.</p>
                <p>Your responses have been recorded and will be reviewed by our team.</p>
                <p>You will be contacted regarding the next steps in the hiring process.</p>
              </div>
              <div className="completion-stats">
                <div className="stat-item">
                  <div className="stat-label">Messages Exchanged</div>
                  <div className="stat-value">{messages.length}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Session Duration</div>
                  <div className="stat-value">{formatTime(interviewState.getDuration())}</div>
                </div>
              </div>
              <div className="completion-actions">
                <button onClick={closeInterviewWindow} className="primary-button">
                  Close Window
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : !interviewState.permissions.camera || !interviewState.permissions.microphone ? (
        /* Permission Request Screen */
        <div className="app-container centered">
          {/* Rules Overlay */}
          {showRules && (
            <div className="rules-overlay">
              <div className="rules-card">
                <h2><AiOutlineInfoCircle /> Interview Requirements & Guidelines</h2>
                <div className="rules-list">
                  <div className="rule-item required">
                    <FaRegCheckCircle className="rule-icon" />
                    <div className="rule-text">
                      <p>Stable Internet Connection Required</p>
                      <small style={{ color: "rgba(255,255,255,0.6)" }}>
                        Minimum 1 Mbps upload/download speed
                      </small>
                    </div>
                    <span className="requirement-badge">Required</span>
                  </div>
                  <div className="rule-item required">
                    <FaVideo className="rule-icon" />
                    <div className="rule-text">
                      <p>Camera Access Permission</p>
                      <small style={{ color: "rgba(255,255,255,0.6)" }}>
                        Clear video feed needed
                      </small>
                    </div>
                    <span className="requirement-badge">Required</span>
                  </div>
                  <div className="rule-item required">
                    <FaMicrophone className="rule-icon" />
                    <div className="rule-text">
                      <p>Microphone Access Permission</p>
                      <small style={{ color: "rgba(255,255,255,0.6)" }}>
                        Clear audio needed
                      </small>
                    </div>
                    <span className="requirement-badge">Required</span>
                  </div>
                  <div className="rule-item">
                    <FaRegCheckCircle className="rule-icon" />
                    <p>Unmute microphone only when speaking</p>
                  </div>
                  <div className="rule-item">
                    <FaRegCheckCircle className="rule-icon" />
                    <p>Mute yourself after completing your answer</p>
                  </div>
                  <div className="rule-item">
                    <FaRegCheckCircle className="rule-icon" />
                    <p>Ensure good lighting and clear background</p>
                  </div>
                  <div className="rule-item">
                    <FaRegCheckCircle className="rule-icon" />
                    <p>Click "End Call" button to finish interview</p>
                  </div>
                </div>
                <div className="rules-footer">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      onChange={() => setShowRules(false)}
                    />
                    <span>Don't show this again</span>
                  </label>
                  <button
                    className="start-rules-button"
                    onClick={() => setShowRules(false)}
                  >
                    I Understand
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Permissions Card */}
          <div className="permissions-card">
            <div className="logo-container">
              <img src={logo} alt="Company Logo" />
            </div>
            <h1>AI Interview Session</h1>
            <div className="permissions-list">
              <div
                className={`permission-item ${interviewState.permissions.camera ? "granted" : ""}`}
                data-permission="camera"
                data-granted={interviewState.permissions.camera}
              >
                <div className="permission-info">
                  <div className="permission-icon">
                    <FaVideo />
                  </div>
                  <div className="permission-text">
                    <div className="permission-title">Camera Access</div>
                    <div className="permission-status">
                      {interviewState.permissions.camera ? "Granted" : "Required"}
                    </div>
                  </div>
                </div>
                <div
                  className={`status-indicator ${interviewState.permissions.camera ? "granted" : "pending"}`}
                >
                  {interviewState.permissions.camera ? "✓" : "!"}
                </div>
              </div>

              <div
                className={`permission-item ${interviewState.permissions.microphone ? "granted" : ""}`}
                data-permission="microphone"
                data-granted={interviewState.permissions.microphone}
              >
                <div className="permission-info">
                  <div className="permission-icon">
                    <FaMicrophone />
                  </div>
                  <div className="permission-text">
                    <div className="permission-title">Microphone Access</div>
                    <div className="permission-status">
                      {interviewState.permissions.microphone ? "Granted" : "Required"}
                    </div>
                  </div>
                </div>
                <div
                  className={`status-indicator ${interviewState.permissions.microphone ? "granted" : "pending"}`}
                >
                  {interviewState.permissions.microphone ? "✓" : "!"}
                </div>
              </div>
            </div>

            <button
              onClick={startInterview}
              className={`start-interview-button enabled`}
              disabled={false}
              title={
                interviewState.isCompleted || interviewState.isTerminated
                  ? "Interview has already been completed"
                  : !interviewState.permissions.camera || !interviewState.permissions.microphone
                  ? "Click to grant camera and microphone permissions"
                  : "Start your interview"
              }
            >
              {interviewState.isCompleted || interviewState.isTerminated
                ? "Interview Completed"
                : "Start Interview"}
            </button>
          </div>
        </div>
      ) : (
        /* Main Interview Interface */
        <div className="app-container">
          <div className={`interview-container ${isSecurityActive ? "security-active" : ""}`}>
            {/* Video Section */}
            <div className="video-section">
              <div className="video-header">
                <img src={logo} alt="Logo" className="video-logo" />
                <div className="timer-container">
                  <BsClock className="timer-icon" />
                  <span>{interviewState.getFormattedTimeRemaining()}</span>
                </div>
              </div>

              <div className="main-video">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className={`video-feed ${!isVideoOn && "hidden"}`}
                />
                {!isVideoOn && (
                  <div className="video-placeholder">
                    <div className="avatar-container">
                      <div className="avatar">
                        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="100" cy="70" fill="none" r="30" stroke="currentColor" strokeWidth="2" style={{ color: '#8b5cf6' }}></circle>
                          <path d="M70,120 Q100,100 130,120 L130,150 Q100,160 70,150 Z" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#8b5cf6' }}></path>
                          <path d="M85,60 Q100,50 115,60" fill="none" opacity="0.7" stroke="currentColor" strokeWidth="1" style={{ color: '#8b5cf6' }}></path>
                          <path d="M90,70 Q100,65 110,70" fill="none" opacity="0.7" stroke="currentColor" strokeWidth="1" style={{ color: '#8b5cf6' }}></path>
                          <path d="M85,80 Q100,85 115,80" fill="none" opacity="0.7" stroke="currentColor" strokeWidth="1" style={{ color: '#8b5cf6' }}></path>
                        </svg>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {sanitizedOverlayText && (
                <div className={`live-transcript-overlay ${captionTone}`}>
                  {sanitizedOverlayText}
                </div>
              )}

              {/* AI Speaking Wave Animation - appears above controls */}
              <div className={`ai-wave-container ${isAITalking ? 'active' : ''}`}>
                <canvas ref={aiWaveCanvasRef} className="ai-wave-canvas"></canvas>
              </div>

              {/* Meeting Controls */}
              <div className="meeting-controls">
                <button
                  onClick={toggleVideo}
                  className={`control-button ${!isVideoOn ? "off" : ""} ${
                    interviewState.isTerminated || interviewState.isCompleted ? "disabled" : ""
                  }`}
                  disabled={interviewState.isTerminated || interviewState.isCompleted}
                  title={interviewState.isTerminated || interviewState.isCompleted ? "Interview has ended" : ""}
                >
                  {isVideoOn ? <BsCameraVideo size={24} /> : <BsCameraVideoOff size={24} />}
                </button>

                {/* Microphone Status Indicator (No manual control - automatic VAD) */}
                <div 
                  className={`mic-status-display ${audioRecording.isRecording ? "recording" : "idle"}`}
                  title={audioRecording.isRecording ? "Microphone is open - Speak now" : "Microphone is closed"}
                >
                  {audioRecording.isRecording ? <BiMicrophone size={24} /> : <BiMicrophoneOff size={24} />}
                  <span className="mic-status-label">
                    Speak Now
                  </span>
                </div>

                <button onClick={stopInterview} className="control-button end-call">
                  <MdCallEnd size={24} />
                </button>
              </div>
            </div>

            {/* Status Indicators */}
            {showLoader && (
              <div className="conversation-loader">
                <div className="loader-dots">
                  <div className="loader-dot"></div>
                  <div className="loader-dot"></div>
                  <div className="loader-dot"></div>
                </div>
              </div>
            )}

            {/* Microphone Permission Warning */}
            {!interviewState.permissions.microphone &&
              interviewState.isActive && (
                <div className="mic-status-indicator warning">
                  <div className="mic-status-content">
                    <BiMicrophoneOff size={20} />
                    <div className="mic-status-text">
                      Microphone permission needed for voice input. Please allow access.
                    </div>
                  </div>
                </div>
              )}



          </div>
        </div>
      )}

      {/* Security Violation Overlay */}
      {securityViolationDetected && (
        <div className="security-violation-overlay">
          <div className="security-violation-content">
            <div className="security-violation-icon">🚨</div>
            <h2 className="security-violation-title">Security Violation Detected</h2>
            <p className="security-violation-message">
              Developer tools or unauthorized actions were detected during the interview.
              <br />
              The session is being terminated for security reasons.
            </p>
            <p className="security-violation-message">
              Please contact the interviewer if you believe this is an error.
            </p>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        draggable={true}
        pauseOnHover={true}
        theme="dark"
        style={{ zIndex: 10000 }}
      />
    </>
  );
}

export default Interview;
